import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChevronFirst, ChevronLast, ChevronLeft, ChevronRight } from 'lucide-react';
import { rememberPageSize } from '@/hooks/usePreferredPageSize';

// Có 20 vì một số danh sách mặc định 20 dòng (ô số dòng trước đó hiện trống)
export const PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100, 200];

interface ERPPaginationProps {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
    unitLabel?: string;   // "bản ghi", "KH", "SP"...
    className?: string;
}

/**
 * ERPPagination — Sticky bottom, First/Prev/Pages/Next/Last.
 * Mobile: compact (chỉ Prev / X / Total / Next).
 */
export function ERPPagination({
    currentPage,
    totalPages,
    totalItems,
    pageSize,
    onPageChange,
    onPageSizeChange,
    unitLabel = 'bản ghi',
    className,
}: ERPPaginationProps) {
    const from = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const to = Math.min(currentPage * pageSize, totalItems);

    // Build page window (max 5 visible page numbers around current)
    const pageWindow = buildPageWindow(currentPage, totalPages);

    return (
        <div
            data-testid="erp-pagination"
            className={cn(
            'sticky bottom-0 z-30 bg-white border-t border-slate-200 px-3 py-1.5',
            'flex items-center justify-between gap-2 shadow-[0_-1px_3px_rgba(0,0,0,0.06)]',
            className
        )}>

            {/* Left: record count + page size selector */}
            <div className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] text-slate-500 hidden sm:block">
                    <span className="font-semibold text-slate-700">{from}–{to}</span>
                    {' / '}
                    <span className="font-medium">{totalItems}</span>{' '}
                    {unitLabel}
                </span>
                {/* Mobile: compact count */}
                <span className="text-[11px] text-slate-500 sm:hidden font-medium">
                    {totalItems} {unitLabel}
                </span>
                <Select
                    value={String(pageSize)}
                    // Ghi nhớ số dòng / trang người dùng chọn cho mọi danh sách (không tự về 20 / 25 khi quay lại)
                    onValueChange={v => { rememberPageSize(Number(v)); onPageSizeChange(Number(v)); onPageChange(1); }}
                >
                    <SelectTrigger className="h-6 w-[58px] text-[11px] border-slate-200" data-testid="erp-page-size">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {PAGE_SIZE_OPTIONS.map(s => (
                            <SelectItem key={s} value={String(s)} className="text-xs">{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Right: navigation */}
            <div className="flex items-center gap-0.5">
                {/* First (desktop only) */}
                <Button
                    variant="ghost" size="icon"
                    className="h-7 w-7 hidden sm:flex"
                    disabled={currentPage <= 1}
                    onClick={() => onPageChange(1)}
                >
                    <ChevronFirst size={14} />
                </Button>

                {/* Prev */}
                <Button
                    variant="ghost" size="icon"
                    className="h-7 w-7"
                    disabled={currentPage <= 1}
                    onClick={() => onPageChange(currentPage - 1)}
                >
                    <ChevronLeft size={14} />
                </Button>

                {/* Page numbers — desktop */}
                <div className="hidden sm:flex items-center gap-0.5">
                    {pageWindow.map((p, i) =>
                        p === '...' ? (
                            <span key={`dots-${i}`} className="w-7 text-center text-[11px] text-slate-400">…</span>
                        ) : (
                            <Button
                                key={p}
                                variant={p === currentPage ? 'default' : 'ghost'}
                                size="icon"
                                className={cn(
                                    'h-7 w-7 text-[11px] font-medium',
                                    p === currentPage && 'bg-blue-600 text-white hover:bg-blue-700'
                                )}
                                onClick={() => onPageChange(p as number)}
                            >
                                {p}
                            </Button>
                        )
                    )}
                </div>

                {/* Page indicator — mobile */}
                <span className="sm:hidden px-2 text-[11px] font-medium text-slate-700 tabular-nums">
                    {currentPage} / {totalPages}
                </span>

                {/* Next */}
                <Button
                    variant="ghost" size="icon"
                    className="h-7 w-7"
                    disabled={currentPage >= totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                >
                    <ChevronRight size={14} />
                </Button>

                {/* Last (desktop only) */}
                <Button
                    variant="ghost" size="icon"
                    className="h-7 w-7 hidden sm:flex"
                    disabled={currentPage >= totalPages}
                    onClick={() => onPageChange(totalPages)}
                >
                    <ChevronLast size={14} />
                </Button>
            </div>
        </div>
    );
}

// ── helpers ───────────────────────────────────────────────────
function buildPageWindow(current: number, total: number): (number | '...')[] {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: (number | '...')[] = [];
    const addPage = (p: number) => { if (!pages.includes(p)) pages.push(p); };
    addPage(1);
    if (current > 3) pages.push('...');
    for (let p = Math.max(2, current - 1); p <= Math.min(total - 1, current + 1); p++) addPage(p);
    if (current < total - 2) pages.push('...');
    addPage(total);
    return pages;
}
